import React, { useCallback } from 'react';
import {
  Box,
  Chip,
  IconButton,
  ListItemButton,
  Typography,
} from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import ViewListIcon from '@mui/icons-material/ViewList';
import { MoreHorizontal as MoreHorizontalIcon } from 'lucide-react';
import { LabourItemGroup } from '../../api/labourItemGroupsApi';

interface LabourGroupListItemProps {
  group: LabourItemGroup;
  onItemClick: (id: string) => void;
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, id: string) => void;
  disabled?: boolean;
}

export const LabourGroupListItem = React.memo(function LabourGroupListItem({
  group,
  onItemClick,
  onMenuOpen,
  disabled,
}: LabourGroupListItemProps) {
  const handleClick = useCallback(
    () => onItemClick(group.id),
    [onItemClick, group.id]
  );

  const handleMenuOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      onMenuOpen(e, group.id);
    },
    [onMenuOpen, group.id]
  );

  const previewText =
    group.itemNamePreview.length > 0
      ? group.itemNamePreview.join(', ') +
        (group.labourItemCount > group.itemNamePreview.length ? ', ...' : '')
      : 'No items';

  return (
    <ListItemButton
      onClick={handleClick}
      disabled={disabled}
      data-testid="labour-group-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        py: 1.5,
        px: { xs: 2, sm: 3 },
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <WorkspacesIcon
        sx={{ fontSize: 20, color: 'text.secondary', flexShrink: 0 }}
      />

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="body2"
          fontWeight={600}
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {group.name}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block',
          }}
        >
          {previewText}
        </Typography>
      </Box>

      <Box
        sx={{
          display: { xs: 'none', sm: 'flex' },
          width: 75,
          justifyContent: 'flex-end',
          flexShrink: 0,
        }}
      >
        <Chip
          icon={<ViewListIcon sx={{ fontSize: 13 }} />}
          label={`${group.labourItemCount} item${group.labourItemCount !== 1 ? 's' : ''}`}
          size="small"
          sx={{
            fontSize: '0.7rem',
            height: 22,
            bgcolor: 'action.hover',
            color: 'text.secondary',
            border: 'none',
            '& .MuiChip-icon': { color: 'text.secondary' },
          }}
        />
      </Box>

      <IconButton
        size="small"
        onClick={handleMenuOpen}
        disabled={disabled}
        data-testid="labour-group-card-menu"
        sx={{ flexShrink: 0 }}
      >
        <MoreHorizontalIcon size={18} />
      </IconButton>
    </ListItemButton>
  );
});
